/* global globalEvents */
import { useEffect, useState, useMemo } from 'preact/hooks'
import { addCartItem, changeQuantity, updateCart, updateCartItem } from 'helpers/cart'
import { eventProps } from 'helpers/global'
import { executeGraphQlQuery } from 'helpers/storefront-graphql-helper'
import { getUnitPerCartonLabel } from 'uses/useWholeSale'
import { getCartQuantity } from 'uses/useSearchspringUtils'
import LoaderSpin from 'snippets/loader-spin/loader-spin'
import ProductVariant from '../product-variant/product-variant'
import Price from '../price/price'
import ProductQuantity from '../product-quantity/product-quantity'

/**
 * @param { Array<{ item: string, qty: string }> } boxContains - where item is a handle
 * @returns { Array<{ sku: string, title: string, qty: string }> }
 */
async function getBoxContainsDetails(boxContains) {
  const aliasedProducts = boxContains.map(
    (x, index) => `
      product${index}: productByHandle(handle: "${x.item}") {
        title
        variants(first: 1){
          edges {
            node {
              sku
            }
          }
        }
      }
    `
  )
  const query = `query { ${aliasedProducts} }`
  const res = await executeGraphQlQuery({ query })
    .then((res) => {
      // console.log('res', res)
      return res.json()
    })
    .then((data) => {
      if (data.errors) {
        throw data
      }
      return data.data
    })
    .catch((err) => {
      console.warn('Could not fetch items for boxContains', err)
      return null
    })

  if (!res?.product0) {
    return []
  }

  const rawProducts = Object.values(res)
  const normalisedProducts = rawProducts.map((x) => {
    const { title, variants } = x
    const sku = variants.edges[0].node.sku
    return {
      title,
      sku
    }
  })
  const lineItemProperties = normalisedProducts.map((x, index) => ({ ...x, qty: boxContains[index].qty }))
  return lineItemProperties
}

function PlpQuickAdd({ available, variants, options, metafields, unitPerCartons, currentVariant, setCurrentVariant, totalPrice, isOnlyVariantDefault, productUrl, isGst = false }) {
  const initCartQuantity = getCartQuantity(currentVariant.id, unitPerCartons[currentVariant?.position ? currentVariant.position - 1 : 0])
  const unitPerCarton = useMemo(() => unitPerCartons[currentVariant?.position ? currentVariant.position - 1 : 0], [currentVariant])
  const maxQuantity = available ? Math.floor(currentVariant.inventory_quantity / unitPerCarton) : 0
  const [isAdding, setIsAdding] = useState(false)
  const [quantity, setQuantity] = useState(initCartQuantity)
  const isAvailable = useMemo(() => available && currentVariant.inventory_quantity > unitPerCarton, [currentVariant])

  const addToCart = async () => {
    setIsAdding(true)
    const properties = {}

    if (metafields.boxContains?.length) {
      const props = await getBoxContainsDetails(metafields.boxContains)
      if (props.length) {
        props.forEach((x) => {
          const title = x.title
          properties[title] = `${x.sku}|${x.qty}`
        })
        properties.productContain = 'true'
      }
    }

    properties._intuitive_cid = SLS_STATE.customer.id

    const cartItem = await addCartItem(currentVariant.id, properties, 1 * unitPerCarton)
    if (cartItem) {
      globalEvents.emit(eventProps.cart.add, cartItem)
      setQuantity(1)
    }
    setIsAdding(false)
  }

  const syncCart = async () => {
    const newCart = await updateCart()
    globalEvents.emit(eventProps.cart.update, newCart)
  }

  const onChangeQuantity = async (newQuantity) => {
    if (newQuantity === 0) {
      await updateCartItem(`${currentVariant.id}`, 0)
      await syncCart()
    } else {
      await updateCartItem(`${currentVariant.id}`, newQuantity * unitPerCarton)
      await syncCart()
    }
  }

  const quantityOfCarton = () => quantity || 1

  useEffect(() => {
    const newQuantity = getCartQuantity(currentVariant.id, unitPerCarton)
    if (quantity !== newQuantity) {
      setQuantity(newQuantity)
    }
  }, [currentVariant])

  useEffect(() => {
    globalEvents.on(eventProps.cart.change, (cartItems) => {
      if (cartItems && cartItems.length > 0) {
        const cartItem = cartItems.find(item => item.id === currentVariant.id)
        if (cartItem) {
          if (cartItem.quantity === 0) {
            setQuantity(0)
          } else {
            setQuantity(cartItem.quantity / unitPerCarton)
          }
        }
      }
    })
  }, [])

  return (
    <>
      {isOnlyVariantDefault ? (
        <span className="h-12 border border-default rounded mt-5 p-4 flex items-center justify-center bg-grey-100 text-sm text-center">Only one size available</span>
      ) : (
        <ProductVariant
          variants={variants}
          options={options}
          updateCurrentVariant={setCurrentVariant}
          currentVariant={currentVariant}
          initVariant={currentVariant}
          className=""
          updateUrl={false}
        />
      )}
      {quantity > 0 ? (
        <div className="mt-3 md:mt-4">
          <ProductQuantity quantity={quantity} setQuantity={setQuantity} max={maxQuantity} min={0} changeQuantity={onChangeQuantity} />
        </div>
      ) : (
        isAvailable ? (
        <button
          type="button"
          className="relative w-full mt-3 tracking-normal md:mt-4 text-label md:text-base button-add-to-bag plp-card-button"
          disabled={!isAvailable}
          onClick={addToCart}
        >
          {isAdding ? <LoaderSpin width={6} height={6} /> : <span>Add to cart</span>}
        </button>) : (
          <a className="relative w-full mt-3 flex-center tracking-normal md:mt-4 text-label md:text-base button-add-to-bag plp-card-button" href={productUrl}>Notify me</a>
        )
      )}

      <div className="flex justify-between mt-4">
        <div className="flex flex-col items-start">
          <span className="text-xs md:text-sm text-grey-700">Total:</span>
          <Price price={totalPrice * quantityOfCarton()} isGst={isGst} classSize="text-sm md:text-base"></Price>
        </div>
        <div className="flex flex-col items-end text-xs md:text-sm">
          <span className="text-grey-700">{getUnitPerCartonLabel(1 * quantityOfCarton(), 'carton')}</span>
          <span className="text-grey-500">({getUnitPerCartonLabel(unitPerCarton * quantityOfCarton(), 'unit')})</span>
        </div>
      </div>
    </>
  )
}

export default PlpQuickAdd
